import { log } from '@domgen/shared/utils/log';
import { captureErrorNavigation } from '../../../services/posthog/capture';
import { EventObject } from '@xstate/store-react';
import { AppRoute } from '../../../enums/appRoutes';
import { AppStatus } from '../../../enums/appStatus';
import { StoreEvent } from '../../../enums/storeEvent';
import { StoreContext } from '../../../models/store/context';

/**
 * @description Session Creation Failure
 * If session creation fails, we want to log this and navigate the user to an error page. This event is triggered by the createSession event if it fails to retrieve a session ID from the server.
 * @param {StoreContext} context - The current store context
 * @param {EventObject} _event - The event object (not used in this event)
 * @param {{ effect: (effect: () => Promise<void>) => void; emit: { navigate: (payload: { path: AppRoute; replace?: boolean }) => void } }} enqueue - An object with an effect function to enqueue side effects and an emit function to emit navigation events
 * @returns {StoreContext} Updated context with app status set to ERROR
 * @sideEffects Logs the session creation failure and navigates the user to the error page
 */
export const sessionFailure = <T extends EventObject | undefined = EventObject>(
  context: StoreContext,
  _event: T,
  enqueue: {
    effect: (effect: () => Promise<void>) => void;
    emit: { navigate: (payload: { path: AppRoute; replace?: boolean }) => void };
  },
): StoreContext => {
  log.error({ message: StoreEvent.SESSION_CREATION_FAILED });
  captureErrorNavigation('session-creation-failed');
  enqueue.emit.navigate({ path: AppRoute.ERROR, replace: true });
  return {
    ...context,
    app: {
      ...context.app,
      status: AppStatus.ERROR,
    },
  };
};
